import type {
    FirmRiskParameterResponse,
    FirmRiskResponse,
    RiskControls,
    RiskParameterData,
    RiskParameterResponse,
    RiskParametersResponse,
} from '@thinkalpha/locates-ws-client';
import { lapi as axios } from 'src/api';

/**
 * HTTP wrappers around the locates-ws `/v1/risk` endpoints (PLAT-12881).
 *
 * Backend swagger:
 *   GET    /v1/risk/firm/{firmId}                      — firm rule + per-category counts
 *   PUT    /v1/risk/firm/{firmId}                      — replace the firm rule (all four limits)
 *   GET    /v1/risk/firm/{firmId}/parameters           — list parameters (optional kind /
 *                                                        dimension filters)
 *   POST   /v1/risk/firm/{firmId}/parameters           — create a parameter
 *   PUT    /v1/risk/firm/{firmId}/parameters/{id}      — replace a parameter
 *   DELETE /v1/risk/firm/{firmId}/parameters/{id}      — delete a parameter
 *
 * The wire states every limit explicitly (`null` = unset), so writes must
 * echo limits the UI does not edit (currently `maxDailySize`) to avoid
 * clobbering them.
 */

export const getFirmRisk = (firmId: string): Promise<FirmRiskResponse> => {
    return axios.get<FirmRiskResponse>(`/v1/risk/firm/${firmId}`).then((x) => x.data);
};

export const putFirmRisk = (firmId: string, limits: RiskControls): Promise<FirmRiskParameterResponse> => {
    return axios.put<FirmRiskParameterResponse>(`/v1/risk/firm/${firmId}`, limits).then((x) => x.data);
};

export const getRiskParameters = (firmId: string): Promise<RiskParametersResponse> => {
    return axios.get<RiskParametersResponse>(`/v1/risk/firm/${firmId}/parameters`).then((x) => x.data);
};

export const createRiskParameter = (firmId: string, parameter: RiskParameterData): Promise<RiskParameterResponse> => {
    return axios.post<RiskParameterResponse>(`/v1/risk/firm/${firmId}/parameters`, parameter).then((x) => x.data);
};

export const updateRiskParameter = (
    firmId: string,
    parameterId: string,
    parameter: RiskParameterData,
): Promise<RiskParameterResponse> => {
    return axios
        .put<RiskParameterResponse>(`/v1/risk/firm/${firmId}/parameters/${parameterId}`, parameter)
        .then((x) => x.data);
};

export const deleteRiskParameter = (firmId: string, parameterId: string): Promise<void> => {
    return axios.delete(`/v1/risk/firm/${firmId}/parameters/${parameterId}`).then(() => undefined);
};
